import { Policy, PolicyBillingSettings } from "@deathstar/types/northstar";
import React from "react";
import { useForm, UseFormReturn, useWatch } from "react-hook-form";
import { BillingContactSelector } from "../experimentalBilling/BillingSettingsDialog/BillingContactSelector";
import { PaymentMethodSelector } from "../experimentalBilling/BillingSettingsDialog/PaymentMethodSelector";
import { useSnackbar } from "../hooks/useSnackbar";
import { PrimaryButton } from "../primaryButton/primaryButton";
import { Context } from "./context";

interface Inputs {
    autoPayEnabled: boolean;
    contactId: number | null;
    contactIds: number[];
    paymentMethodId: string | null;
    specialPaymentTerms: string | null;
    upcomingPaymentActionEnabled: boolean;
    paymentDueActionEnabled: boolean;
    preCancellationActionEnabled: boolean;
}

export type Form = UseFormReturn<Inputs>;

export function BillingSettings({
    policy,
    billingSettings,
}: {
    policy: Policy;
    billingSettings?: PolicyBillingSettings;
}): JSX.Element | null {
    const { billingInfo, contacts, updateBillingSettings } = React.useContext(Context);
    const [isSaving, setIsSaving] = React.useState(false);
    const form: Form = useForm<Inputs>({
        defaultValues: {
            autoPayEnabled: billingSettings?.autoPayEnabled || false,
            contactId: billingSettings?.contactId || null,
            contactIds: PolicyBillingSettings.getContactIds(billingSettings),
            paymentMethodId: billingSettings?.paymentMethodId || null,
            upcomingPaymentActionEnabled: true,
            paymentDueActionEnabled: true,
            preCancellationActionEnabled: true,
        },
    });
    const validContacts = React.useMemo(() => {
        return contacts?.filter((contact) => contact.email);
    }, [contacts]);
    const contactIds = useWatch({
        control: form.control,
        name: "contactIds",
    });
    const paymentMethodId = useWatch({
        control: form.control,
        name: "paymentMethodId",
    });

    const isDisabled = React.useMemo(() => {
        if (!billingSettings?.autoPayEnabled) return false;
        if (
            JSON.stringify(PolicyBillingSettings.getContactIds(billingSettings)) !== JSON.stringify(contactIds) ||
            billingSettings?.paymentMethodId !== paymentMethodId
        ) {
            return false;
        }
        return true;
    }, [billingSettings, contactIds, paymentMethodId]);

    if (!billingInfo || !billingSettings || !contacts || !billingSettings?.autoPayEnabled || !updateBillingSettings) return null;

    return (
        <div className="flex items-start gap-4 justify-self-end ">
            <div className="grid flex-1 grid-cols-2 gap-4">
                <BillingContactSelector form={form} contacts={validContacts} disabled={false} />
                <PaymentMethodSelector form={form} billingInfo={billingInfo} disabled={false} />
            </div>
            <PrimaryButton
                className="h-min bg-navigator-primary"
                disabled={isDisabled || isSaving || !contactIds?.length}
                onClick={() => {
                    form.handleSubmit(async (data) => {
                        setIsSaving(true);
                        await updateBillingSettings(
                            { policyId: policy.id },
                            {
                                ...billingSettings,
                                ...data,
                            }
                        );
                        setIsSaving(false);
                        useSnackbar.add("Billing settings updated successfully", {
                            variant: "success",
                        });
                    })();
                }}
            >
                Save
            </PrimaryButton>
        </div>
    );
}
